import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { CellClickedEvent } from 'ag-grid-enterprise'
import { Play } from 'lucide-react'

import { toLongDateString } from '@fortem-monorepo/js-utilities'
import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { PricingInterface } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { RunNPVModal } from '@/components/dialogs/RunNPVModal'
import { RunRiskModal } from '@/components/dialogs/RunRiskModal'
import { RunScenarioModal } from '@/components/dialogs/RunScenarioModal'
import { FLOW } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import {
  pricingInputsColDefs,
  pricingRequestsColDefs,
} from '@/pages/flow/pricing-interface/col-defs'

export interface PricingInterfacePageProps {}

export const PricingInterfacePage: React.FC<PricingInterfacePageProps> = ({}) => {
  const [params, setParams] = useState<PricingInterface.GetStresstestResultsParams | undefined>()

  const [runningNPV, setRunningNPV] = useState(false)
  const [runningRisk, setRunningRisk] = useState(false)
  const [runningScenario, setRunningScenario] = useState(false)

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['pricing-interface', 'state'],
        queryFn: () => api.pricingInterface.getState(),
        refetchInterval: 10_000,
      },
      {
        queryKey: ['pricing-interface', 'inputs-results', { params }],
        queryFn: () => api.pricingInterface.stressTests.getResults(params!),
        enabled: params !== undefined,
      },
    ],
  })

  const [pricingRequests, pricingResults] = queries

  const requestData = useMemo(() => {
    return pricingRequests.data ?? []
  }, [pricingRequests.data])

  const resultsData = useMemo(() => {
    return pricingResults.data ?? []
  }, [pricingResults.data])

  return (
    <Page
      metadata={{ title: 'Pricing Interface' }}
      permissions={FLOW.PRICING_INTERFACE.permissions}
    >
      <CanAccess permissions={[PERMISSIONS.WRITE_PRICING_INTERFACE]}>
        <RunNPVModal open={runningNPV} onOpenChange={setRunningNPV} />
        <RunRiskModal open={runningRisk} onOpenChange={setRunningRisk} />
        <RunScenarioModal open={runningScenario} onOpenChange={setRunningScenario} />
        <HeaderConfigBar>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <Play />
                  Run stresstests
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setRunningNPV(true)}>NPV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setRunningRisk(true)}>Risk</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setRunningScenario(true)}>
                  Scenario
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </HeaderConfigBar>
      </CanAccess>
      <ResizablePanelGroup direction={'vertical'}>
        <ResizablePanel>
          <AgGridWidget
            query={pricingRequests}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{ title: 'Pricing Requests State (refresh 10s)' }}
            grid={{
              domLayout: 'normal',
              rowData: requestData,
              columnDefs: pricingRequestsColDefs,
              onCellClicked: (
                params: CellClickedEvent<PricingInterface.GetStresstestInstanceQueueData[number]>,
              ) => {
                const batchid = params.data?.batchid!
                const value_date = params.data?.value_date!
                setParams({
                  batchid,
                  value_date,
                })
              },
            }}
          />
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <AgGridWidget
            query={pricingResults}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{
              title: `Pricing Inputs & Results${params !== undefined ? ` for ${toLongDateString(new Date(params.value_date))} batch ${params.batchid}` : ''}`,
            }}
            grid={{ domLayout: 'normal', rowData: resultsData, columnDefs: pricingInputsColDefs }}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
